import {
  BULK_ACTION_MAX_LEADS,
  formatCustomValue,
  LEAD_EXPORT_COLUMNS,
  MANAGER_ROLES,
  PIPELINE_STAGE_ORDER,
  type LeadExportRow,
  type LeadListItem,
  type SavedViewDto,
  CONTACT_STATE_LABELS,
} from '@performance-crm/shared';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Columns3, Download, Loader2, Plus, Tag, UserCog } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AttentionBadge,
  LeadFollowupBadge,
  ScoreBadge,
  StageBadge,
  ContactBadge,
} from '@/components/leads/badges';
import { BulkTagDialog } from '@/components/leads/BulkTagDialog';
import { LeadFilters } from '@/components/leads/LeadFilters';
import { applyLeadFilter } from '@/components/leads/leadFilterRules';
import { TagChips } from '@/components/leads/TagChips';
import { useCompanyScope } from '@/lib/useCompanyScope';
import { SavedViews } from '@/components/leads/SavedViews';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { bulkAssignSummary } from '@/lib/bulkSummary';
import { fmt, INTEREST_LABEL, STAGE_LABEL } from '@/lib/format';
import { useAdvisors, useBulkAssign, useCatalogs, useExportLeads, useLeads } from '@/lib/queries';
import { downloadWorkbook, excelDateTime, type SheetCell } from '@/lib/spreadsheet';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';
import { ViewSwitch } from '@/components/leads/ViewSwitch';
import { useToast } from '@/components/ui/toast-context';

const FILTER_KEYS = [
  'companyId',
  'projectId',
  'advisorId',
  'followupState',
  'pipelineStage',
  'mediumCategory',
  'q',
  'sort',
  'overdueBand',
  'overdueDays',
  'actions',
  'contactState',
  'capturedFrom',
  'capturedTo',
  'tagIds',
  'unassigned',
  'status',
];
const STORAGE_KEY = 'crm.leads.columns';
const ROW_HEIGHT = 52;

const COLUMNS: ColumnDef<LeadListItem>[] = [
  {
    id: 'firstName',
    header: 'Nombres',
    enableHiding: false,
    size: 170,
    cell: ({ row }) => (
      <div className="flex min-w-0 items-center gap-1">
        <span className="truncate font-medium">{row.original.firstName}</span>
        {row.original.isRecontact && <Badge tone="muted">Recontacto</Badge>}
      </div>
    ),
  },
  {
    id: 'lastName',
    header: 'Apellidos',
    size: 160,
    cell: ({ row }) => <span className="block truncate">{row.original.lastName ?? '—'}</span>,
  },
  {
    id: 'phone',
    header: 'Celular',
    size: 130,
    cell: ({ row }) => (
      <span className="block truncate tabular-nums">{row.original.phone ?? '—'}</span>
    ),
  },
  {
    id: 'email',
    header: 'Correo',
    size: 190,
    cell: ({ row }) => <span className="block truncate">{row.original.email ?? '—'}</span>,
  },
  {
    id: 'project',
    header: 'Proyecto',
    size: 140,
    cell: ({ row }) => <span className="block truncate">{row.original.projectName}</span>,
  },
  {
    id: 'tags',
    header: 'Etiquetas',
    size: 170,
    cell: ({ row }) =>
      row.original.tags.length ? <TagChips tags={row.original.tags} max={2} /> : '—',
  },
  {
    id: 'stage',
    header: 'Etapa',
    size: 120,
    cell: ({ row }) => <StageBadge stage={row.original.pipelineStage} />,
  },
  {
    id: 'followup',
    header: 'Seguimiento',
    size: 130,
    cell: ({ row }) => <LeadFollowupBadge lead={row.original} />,
  },
  {
    id: 'attention',
    header: 'Bot',
    size: 190,
    cell: ({ row }) =>
      row.original.attention ? (
        <AttentionBadge attention={row.original.attention} />
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: 'nextTask',
    header: 'Próxima tarea',
    size: 150,
    cell: ({ row }) => (
      <span className="whitespace-nowrap">{fmt.dateTime(row.original.nextTaskAt)}</span>
    ),
  },
  {
    id: 'lastContact',
    header: 'Último contacto',
    size: 150,
    cell: ({ row }) => (
      <span className="whitespace-nowrap">{fmt.dateTime(row.original.lastContactAt)}</span>
    ),
  },
  {
    id: 'lastRecontact',
    header: 'Último recontacto',
    size: 150,
    cell: ({ row }) => (
      <span className="whitespace-nowrap">{fmt.dateTime(row.original.lastRecontactAt)}</span>
    ),
  },
  {
    id: 'gestiones',
    header: 'Gestiones',
    size: 90,
    cell: ({ row }) => <span className="tabular-nums">{row.original.gestiones}</span>,
  },
  {
    id: 'contact',
    header: 'Contacto',
    size: 170,
    cell: ({ row }) => (
      <ContactBadge
        state={row.original.contactState}
        attempts={row.original.contactAttempts}
        reason={row.original.lastNonEffectiveReasonLabel ?? row.original.lastNonEffectiveReason}
      />
    ),
  },
  {
    id: 'advisor',
    header: 'Asesor',
    size: 140,
    cell: ({ row }) => (
      <span className="block truncate">
        {row.original.advisorName ?? <span className="text-muted-foreground">Sin asignar</span>}
      </span>
    ),
  },
  {
    id: 'subStatus',
    header: 'Sub-estado',
    size: 130,
    cell: ({ row }) => row.original.subStatus ?? '—',
  },
  {
    id: 'interest',
    header: 'Interés',
    size: 110,
    cell: ({ row }) => INTEREST_LABEL[row.original.interestLevel],
  },
  {
    id: 'captured',
    header: 'Captación',
    size: 130,
    cell: ({ row }) => (
      <div className="min-w-0">
        <p className="whitespace-nowrap">{fmt.date(row.original.capturedAt)}</p>
        <p className="text-muted-foreground truncate text-xs">{row.original.medium ?? '—'}</p>
      </div>
    ),
  },
  {
    id: 'category',
    header: 'Categoría',
    size: 120,
    cell: ({ row }) => row.original.originalMediumCategory ?? '—',
  },
  {
    id: 'stageChanged',
    header: 'En etapa desde',
    size: 130,
    cell: ({ row }) => fmt.date(row.original.pipelineStageChangedAt),
  },
  {
    id: 'score',
    header: 'Puntaje',
    size: 80,
    cell: ({ row }) => <ScoreBadge score={row.original.score} />,
  },
];
/** Valor de cada celda del Excel: fechas reales (dd/mm/aaaa hh:mm en la zona de la empresa), etiquetas en español y Sí / No. */
function exportCell(row: LeadExportRow, key: keyof LeadExportRow, tz: string): SheetCell {
  const v = row[key];
  if (key === 'capturedAt' || key === 'lastContactAt' || key === 'nextTaskAt')
    return excelDateTime(v as string | null, tz);
  if (key === 'stage') return STAGE_LABEL[v as keyof typeof STAGE_LABEL] ?? String(v);
  if (key === 'contact')
    return CONTACT_STATE_LABELS[v as keyof typeof CONTACT_STATE_LABELS] ?? String(v);
  if (key === 'interest') return INTEREST_LABEL[v as keyof typeof INTEREST_LABEL] ?? String(v);
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (typeof v === 'object' && v !== null) return null;
  return v as string | number | null;
}

const DEFAULT_HIDDEN: VisibilityState = {
  email: false,
  attention: false,
  lastContact: false,
  lastRecontact: false,
  subStatus: false,
  interest: false,
  category: false,
  stageChanged: false,
};

/** Configuraciones guardadas antes de separar "Contacto" en Nombres / Apellidos / Celular siguen valiendo. */
function normalizeVisibility(v: VisibilityState): VisibilityState {
  const { contact: _legacy, ...rest } = v;
  return {
    ...rest,
    lastName: rest.lastName ?? true,
    phone: rest.phone ?? true,
    email: rest.email ?? false,
  };
}

function loadColumns(): VisibilityState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? normalizeVisibility(JSON.parse(raw) as VisibilityState) : DEFAULT_HIDDEN;
  } catch {
    return DEFAULT_HIDDEN;
  }
}

export function LeadsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { active: activeCompany } = useCompanyScope();
  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    for (const key of FILTER_KEYS) {
      const v = params.get(key);
      if (v) p.set(key, v);
    }
    // Filtros por campo personalizado (cf_<clave>) tal como vienen de la barra o de una vista guardada.
    for (const [k, v] of params.entries()) if (k.startsWith('cf_') && v) p.set(k, v);
    if (!p.get('companyId') && activeCompany) p.set('companyId', activeCompany);
    p.set('limit', '100');
    return p;
  }, [params, activeCompany]);
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useLeads(queryParams);
  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(loadColumns);
  const { user } = useAuth();
  const toast = useToast();
  const companyForBulk = queryParams.get('companyId') ?? activeCompany;
  const tz = useCompanyTimezone(companyForBulk);
  const total = data?.pages[0]?.total;
  const isManager = !!user?.memberships.some(
    (m) => (!companyForBulk || m.companyId === companyForBulk) && MANAGER_ROLES.includes(m.role),
  );
  const { data: advisorsForBulk } = useAdvisors(isManager ? companyForBulk : undefined);
  const bulk = useBulkAssign();
  const exporter = useExportLeads();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Excel de la selección o de todo lo filtrado (tope 5 000); la API registra quién exportó. */
  const runExport = async (mode: 'selection' | 'filters') => {
    if (mode === 'filters') {
      const n = total ?? 0;
      if (n === 0) {
        toast.error('No hay leads para exportar con estos filtros');
        return;
      }
      if (
        !confirm(
          `Se exportarán ${Math.min(n, 5000)} de ${n} leads con los filtros actuales. ¿Continuar?`,
        )
      )
        return;
    }
    try {
      const filters = Object.fromEntries(
        [...queryParams.entries()].filter(([k]) => k !== 'limit' && k !== 'cursor'),
      );
      const r = await exporter.mutateAsync(
        mode === 'selection' ? { leadIds: [...selected] } : { filters },
      );
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadWorkbook(
        [
          {
            name: 'Leads',
            header: [
              ...LEAD_EXPORT_COLUMNS.map(([, label]) => label),
              ...r.customFields.map((f) => f.label),
            ],
            rows: r.rows.map((row) => [
              ...LEAD_EXPORT_COLUMNS.map(([key]) => exportCell(row, key, tz)),
              ...r.customFields.map((f) => {
                const v = row.custom[f.key];
                if (v === null || v === undefined) return null;
                if (f.type === 'SI_NO') return v ? 'Sí' : 'No';
                return v as string | number;
              }),
            ]),
          },
        ],
        `leads-${stamp}.xlsx`,
      );
      toast.success(
        `${r.rows.length} lead${r.rows.length === 1 ? '' : 's'} exportado${r.rows.length === 1 ? '' : 's'}`,
        r.truncated
          ? `Solo salieron los primeros ${r.rows.length} de ${r.total}: afina los filtros para el resto.`
          : undefined,
      );
    } catch (err) {
      toast.error('No se pudo exportar', (err as Error).message);
    }
  };
  const [bulkAdvisor, setBulkAdvisor] = useState('');
  const [tagging, setTagging] = useState(false);
  const toggleSelected = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allVisibleSelected = items.length > 0 && items.every((l) => selected.has(l.id));
  // Lote 6 · I4 (P19): la selección no sobrevive a un cambio de empresa, filtros, búsqueda o vista guardada; si no,
  // Reasignar, Etiquetar o Exportar actuarían sobre leads que ya no se ven (incluso de otra empresa).
  const queryKey = queryParams.toString();
  useEffect(() => {
    setSelected(new Set());
    setTagging(false);
  }, [queryKey]);
  // Reasignar y etiquetar aceptan hasta 500 por acción (el Excel conserva su tope de 5 000).
  const overLimit = selected.size > BULK_ACTION_MAX_LEADS;
  const limitNotice = `Máximo ${BULK_ACTION_MAX_LEADS} por acción; afina el filtro`;
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columnVisibility));
    } catch {
      /* sin almacenamiento */
    }
  }, [columnVisibility]);

  // Columnas por campo personalizado de la empresa filtrada (o la activa).
  const { data: columnCatalogs } = useCatalogs(companyForBulk);
  const columns = useMemo<ColumnDef<LeadListItem>[]>(() => {
    const custom = (columnCatalogs?.customFields ?? []).map((f): ColumnDef<LeadListItem> => ({
      id: `custom.${f.key}`,
      header: f.label,
      size: 140,
      cell: ({ row }) => (
        <span className="block truncate">{formatCustomValue(f, row.original.custom[f.key])}</span>
      ),
    }));
    return [...COLUMNS, ...custom];
  }, [columnCatalogs]);
  const table = useReactTable({
    data: items,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });
  const rows = table.getRowModel().rows;
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });
  const virtualRows = virtualizer.getVirtualItems();
  // Cabecera y filas comparten la misma plantilla de columnas: anchos mínimos por columna que crecen
  // en proporción (la de contacto el doble) para que todo quede alineado sin depender del contenido.
  const leafColumns = table.getVisibleLeafColumns();
  const gridTemplateColumns = [
    ...(isManager ? ['40px'] : []),
    ...leafColumns.map((c) => `minmax(${c.getSize()}px, ${c.id === 'contact' ? '2fr' : '1fr'})`),
  ].join(' ');
  const gridMinWidth = (isManager ? 40 : 0) + leafColumns.reduce((sum, c) => sum + c.getSize(), 0);
  useEffect(() => {
    const last = virtualRows[virtualRows.length - 1];
    if (last && last.index >= rows.length - 15 && hasNextPage && !isFetchingNextPage)
      void fetchNextPage();
  }, [virtualRows, rows.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  /** La tabla vive en /pipeline?vista=tabla: cualquier cambio de filtros conserva la vista. */
  const keepView = (next: URLSearchParams) => {
    const vista = params.get('vista');
    if (vista) next.set('vista', vista);
    return next;
  };
  const set = (key: string, value: string) =>
    setParams(applyLeadFilter(params, key, value), { replace: true });
  const applyView = (v: SavedViewDto) => {
    setParams(keepView(new URLSearchParams(v.filters)), { replace: true });
    if (v.columns) {
      const cols = new Set(v.columns);
      // Vistas guardadas con la antigua columna "Contacto": equivale a nombres, apellidos y celular.
      if (cols.has('contact')) for (const id of ['firstName', 'lastName', 'phone']) cols.add(id);
      setColumnVisibility(Object.fromEntries(COLUMNS.map((c) => [c.id!, cols.has(c.id!)])));
    }
  };
  const currentFilters = Object.fromEntries(
    [...FILTER_KEYS, ...[...params.keys()].filter((k) => k.startsWith('cf_'))]
      .map((k) => [k, params.get(k) ?? ''])
      .filter(([, v]) => v),
  );
  const visibleColumns = table.getVisibleLeafColumns().map((c) => c.id);
  const activeFilters = Object.keys(currentFilters).filter((k) => k !== 'sort').length;

  return (
    <div className="flex h-[calc(100vh-7.5rem)] flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
          <p className="text-muted-foreground text-sm" data-testid="leads-total">
            {total !== undefined ? `${fmt.number(total)} leads` : `${items.length} cargados`}
            {total !== undefined && items.length < total ? ` · ${items.length} cargados` : ''}
            {hasNextPage ? ' · desplázate para cargar más' : ''} · {activeFilters} filtro
            {activeFilters === 1 ? '' : 's'}
            {activeFilters > 0 && (
              <button
                type="button"
                className="ml-1 underline"
                onClick={() => setParams(keepView(new URLSearchParams()), { replace: true })}
              >
                limpiar
              </button>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SavedViews
            scope="LEADS"
            currentFilters={currentFilters}
            currentColumns={visibleColumns}
            onApply={applyView}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                aria-label="Columnas"
                className="hidden md:inline-flex"
              >
                <Columns3 className="h-4 w-4" /> Columnas
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-48">
              <DropdownMenuLabel>Columnas visibles</DropdownMenuLabel>
              {table.getAllLeafColumns().map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={c.getIsVisible()}
                  disabled={!c.getCanHide()}
                  onCheckedChange={(v) => c.toggleVisibility(!!v)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {String(c.columnDef.header)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {isManager && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void runExport('filters')}
              disabled={exporter.isPending}
              title="Excel con los leads que cumplen los filtros actuales"
              data-testid="export-filtered"
            >
              <Download className="h-4 w-4" /> Exportar
            </Button>
          )}
          <ViewSwitch view="tabla" params={params} />
          <Button asChild size="sm">
            <Link to="/leads/new">
              <Plus className="h-4 w-4" /> Nuevo lead
            </Link>
          </Button>
        </div>
      </div>

      <LeadFilters
        params={params}
        onChange={set}
        extra={
          <>
            <Select
              value={params.get('pipelineStage') ?? ''}
              onChange={(e) => set('pipelineStage', e.target.value)}
              aria-label="Etapa"
            >
              <option value="">Cualquier etapa</option>
              {PIPELINE_STAGE_ORDER.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABEL[s]}
                </option>
              ))}
            </Select>
            <Select
              value={params.get('sort') ?? 'captured_desc'}
              onChange={(e) => set('sort', e.target.value)}
              aria-label="Orden"
            >
              <option value="captured_desc">Más recientes</option>
              <option value="captured_asc">Más antiguos</option>
              <option value="reference_asc">Vencidos hace más tiempo</option>
              <option value="next_task_asc">Próxima tarea más cercana</option>
              <option value="stage_changed_desc">Cambio de etapa reciente</option>
              <option value="recontact_desc">Recontacto reciente</option>
            </Select>
          </>
        }
      />

      {error && (
        <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          {(error as Error).message}
        </p>
      )}

      {isManager && selected.size > 0 && (
        <div
          className="border-primary/30 bg-primary/5 flex flex-wrap items-center gap-2 rounded-lg border p-2 text-sm"
          data-testid="bulk-bar"
        >
          <span className="font-medium">
            {selected.size} seleccionado{selected.size === 1 ? '' : 's'}
          </span>
          {overLimit && (
            <span className="text-destructive text-xs font-medium" data-testid="bulk-limit">
              {limitNotice}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => void runExport('selection')}
            disabled={exporter.isPending}
            data-testid="export-selected"
          >
            <Download className="h-4 w-4" /> Exportar a Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9"
            onClick={() => setTagging(true)}
            disabled={overLimit}
            title={overLimit ? limitNotice : undefined}
            data-testid="bulk-tag"
          >
            <Tag className="h-4 w-4" /> Etiquetar
          </Button>
          <Select
            value={bulkAdvisor}
            onChange={(e) => setBulkAdvisor(e.target.value)}
            aria-label="Reasignar a"
            className="h-9 w-56"
            disabled={overLimit}
            title={overLimit ? limitNotice : undefined}
          >
            <option value="">Reasignar a…</option>
            <option value="__none__">Sin asesor (bolsa)</option>
            {advisorsForBulk
              ?.filter((a) => a.kind === 'HUMAN')
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.fullName}
                </option>
              ))}
          </Select>
          <Button
            size="sm"
            disabled={!bulkAdvisor || bulk.isPending || overLimit}
            title={overLimit ? limitNotice : undefined}
            onClick={() =>
              bulk
                .mutateAsync({
                  leadIds: [...selected],
                  advisorId: bulkAdvisor === '__none__' ? null : bulkAdvisor,
                  reason: 'Reasignación masiva',
                })
                .then((r) => {
                  // Cuántos sí, cuántos ya lo tenían (no es un cambio ni un error) y por qué no los demás, que
                  // quedan seleccionados; con fallos, tono de error para que dure lo suficiente para leerlo.
                  const name = new Map(items.map((l) => [l.id, l.contactName]));
                  toast.push(bulkAssignSummary(r, (id) => name.get(id)));
                  setSelected(new Set(r.failed.map((f) => f.id)));
                  setBulkAdvisor('');
                })
                .catch((e: Error) => toast.error('No se pudo reasignar', e.message))
            }
            data-testid="bulk-assign"
          >
            <UserCog className="h-4 w-4" /> Reasignar
          </Button>
          <button
            type="button"
            className="ml-auto text-xs underline"
            onClick={() => setSelected(new Set())}
          >
            Quitar selección
          </button>
        </div>
      )}

      {tagging && companyForBulk && (
        <BulkTagDialog
          companyId={companyForBulk}
          leadIds={[...selected]}
          onClose={() => setTagging(false)}
          onDone={() => {
            setTagging(false);
            setSelected(new Set());
          }}
        />
      )}

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto md:hidden" data-testid="leads-cards">
        {items.map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => navigate(`/leads/${l.id}`)}
            className="bg-card block w-full rounded-lg border p-3 text-left text-sm shadow-sm"
            data-testid="lead-card"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium">{l.contactName}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {l.projectName} · {l.advisorName ?? 'Sin asignar'}
                </p>
                {l.tags.length > 0 && <TagChips tags={l.tags} max={3} />}
              </div>
              <StageBadge stage={l.pipelineStage} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <LeadFollowupBadge lead={l} />
              <ScoreBadge score={l.score} />
              <span className="text-muted-foreground ml-auto text-xs">
                {l.nextTaskAt ? `próx. ${fmt.dateTime(l.nextTaskAt)}` : fmt.date(l.capturedAt)}
              </span>
            </div>
          </button>
        ))}
        {!isLoading && items.length === 0 && (
          <p className="text-muted-foreground p-6 text-center text-sm">
            No hay leads con estos filtros.
          </p>
        )}
        {hasNextPage && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
          </Button>
        )}
      </div>
      <div
        ref={parentRef}
        className="bg-card hidden min-h-0 flex-1 overflow-auto rounded-lg border md:block"
        data-testid="leads-table"
      >
        <table className="w-full text-sm" style={{ display: 'grid', minWidth: gridMinWidth }}>
          <thead
            className="bg-muted/90 text-muted-foreground sticky top-0 z-10 text-left text-xs uppercase tracking-wide backdrop-blur"
            style={{ display: 'grid' }}
          >
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} style={{ display: 'grid', gridTemplateColumns }}>
                {isManager && (
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar los ${items.length} cargados`}
                      title={`Seleccionar los ${items.length} cargados`}
                      checked={allVisibleSelected}
                      disabled={items.length === 0}
                      onChange={(e) =>
                        setSelected(e.target.checked ? new Set(items.map((l) => l.id)) : new Set())
                      }
                      className="h-4 w-4"
                    />
                  </th>
                )}
                {hg.headers.map((h) => (
                  <th key={h.id} className="min-w-0 truncate px-3 py-2">
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            style={{
              display: 'grid',
              height: `${virtualizer.getTotalSize()}px`,
              position: 'relative',
            }}
          >
            {isLoading &&
              [0, 1, 2, 3, 4].map((i) => (
                <tr key={i} style={{ display: 'flex' }}>
                  <td className="w-full px-3 py-3">
                    <Skeleton className="h-5 w-full" />
                  </td>
                </tr>
              ))}
            {virtualRows.map((vr) => {
              const row = rows[vr.index]!;
              return (
                <tr
                  key={row.id}
                  data-index={vr.index}
                  ref={(el) => virtualizer.measureElement(el)}
                  className="hover:bg-accent/50 cursor-pointer border-t"
                  style={{
                    display: 'grid',
                    gridTemplateColumns,
                    alignItems: 'center',
                    position: 'absolute',
                    transform: `translateY(${vr.start}px)`,
                    width: '100%',
                  }}
                  onClick={() => navigate(`/leads/${row.original.id}`)}
                  data-testid="lead-row"
                >
                  {isManager && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label="Seleccionar lead"
                        checked={selected.has(row.original.id)}
                        onChange={() => toggleSelected(row.original.id)}
                        className="h-4 w-4"
                      />
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="min-w-0 px-3 py-2">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
            {!isLoading && rows.length === 0 && (
              <tr style={{ display: 'flex' }}>
                <td className="text-muted-foreground w-full px-3 py-10 text-center text-sm">
                  No hay leads con estos filtros. Prueba a limpiar los filtros o crea uno nuevo.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {isFetchingNextPage && (
          <p className="text-muted-foreground flex items-center justify-center gap-2 p-2 text-xs">
            <Loader2 className="h-3 w-3 animate-spin" /> Cargando más…
          </p>
        )}
      </div>
    </div>
  );
}
